import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { CADENCES, COMPARISONS, FILTER_DEFINITIONS, type Kpis, type LebtFilters, METRICS, SPLITS, TABS } from './model';

const VISIBLE_ROWS = 25;

type PageSection = {
  title: string;
  rows: Array<{ label: string; values: Record<string, number | string | null> }>;
  truncated: boolean;
};

export function lebtCommercialPageView(input: {
  ready: boolean;
  filters: LebtFilters;
  options: Record<string, Array<string>>;
  period: string;
  comparisonPeriod: string;
  current: Kpis | null;
  previous: Kpis | null;
  sections: Array<PageSection>;
}): PageView {
  const { filters, options } = input;
  const filterSchema: Record<string, PageField> = {
    tab: { type: 'enum', values: TABS.map((tab) => tab.value) },
    from: { type: 'date' },
    to: { type: 'date' },
    comparison: { type: 'enum', values: COMPARISONS.map((item) => item.value) },
    compareFrom: { type: 'date' },
    compareTo: { type: 'date' },
    cadence: { type: 'enum', values: CADENCES },
    metric: { type: 'enum', values: METRICS },
    split: { type: 'enum', values: SPLITS },
    ...Object.fromEntries(
      FILTER_DEFINITIONS.map(({ key }) => [key, { type: 'enum', values: [null, ...(options[key] ?? [])] }]),
    ),
  };
  return {
    title: 'LEBT Commercial Dashboard',
    ready: input.ready,
    dataMode: 'live analytics.lebt_sales_with_companies and exports_salesforce.lebt_business_activity_status',
    filters: {
      tab: filters.tab,
      from: filters.from,
      to: filters.to,
      comparison: filters.comparison,
      compareFrom: filters.compareFrom,
      compareTo: filters.compareTo,
      cadence: filters.cadence,
      metric: filters.metric,
      split: filters.split,
      ...Object.fromEntries(
        FILTER_DEFINITIONS.map(({ key }) => [
          key,
          filters.selections[key].length === 1 ? filters.selections[key][0] : null,
        ]),
      ),
    },
    filterSchema,
    activeSelections: Object.fromEntries(
      FILTER_DEFINITIONS.filter(({ key }) => filters.selections[key].length).map(({ key }) => [
        key,
        filters.selections[key],
      ]),
    ),
    period: input.period,
    comparisonPeriod: input.comparisonPeriod,
    summary: input.current,
    comparisonSummary: input.previous,
    visibleSections: input.sections.map((section) => ({
      title: section.title,
      totalRows: section.rows.length,
      rows: section.rows.slice(0, VISIBLE_ROWS).map((row) => ({ label: row.label, ...row.values })),
      truncated: section.truncated || section.rows.length > VISIBLE_ROWS,
    })),
    definitions: {
      source:
        'LEBT order items from lux-analytics-prod.analytics.lebt_sales_with_companies and registered businesses from exports_salesforce.lebt_business_activity_status, queried live; both rebuilt daily.',
      dates:
        'Sydney dates: the transaction date, or the order item time for items with no matched sale. The comparison is the same number of days before (PP), the same dates last year (LY) or custom dates.',
      measures:
        'Gross Sales sums gross_sales_aud (certified TTV). Margin sums margin_aud. Orders are distinct order_bk; Purchasers distinct businesses. Gross Sales incl GC and PC uses item_total for gift cards and purchased credit and is not TTV; Orders Summary and Weekly Summary use it, as the workbook does.',
      filters:
        'Product starts on the workbook selection that leaves out gift cards, purchased credit and unmatched items. Each filter here accepts one value, or null for every value.',
    },
  };
}

/** Applies a validated askRichard edit: one value per filter, or null for every value. */
export function editFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: LebtFilters,
): Partial<LebtFilters> {
  validatePageFilters(changes, view.filterSchema);
  const next: Partial<LebtFilters> = {};
  for (const key of [
    'tab',
    'from',
    'to',
    'comparison',
    'compareFrom',
    'compareTo',
    'cadence',
    'metric',
    'split',
  ] as const) {
    if (key in changes) {
      Object.assign(next, { [key]: changes[key] });
    }
  }
  const selections = { ...filters.selections };
  let changed = false;
  for (const { key } of FILTER_DEFINITIONS) {
    if (key in changes) {
      const value = changes[key] as string | null;
      selections[key] = value === null ? [] : [value];
      changed = true;
    }
  }
  if (changed) {
    next.selections = selections;
  }
  return next;
}
